import { useEffect, useMemo, useRef, useState } from "react";
import { EventModal } from "./components/event-modal";
import { Toolbar } from "./components/toolbar";
import { buildFeedUrl, fetchEvents, fetchEventsAllPages } from "./lib/api";
import type { EmbedEvent, PaginatedResponse } from "./lib/api";
import type { View, WidgetConfig } from "./lib/config";
import { matchesQuery } from "./lib/search";
import { AgendaView } from "./views/agenda-view";
import { ListView } from "./views/list-view";
import { MonthlyView } from "./views/monthly-view";
import { PinboardView } from "./views/pinboard-view";

type WidgetProps = {
  config: WidgetConfig;
};

function initialAnchor(config: WidgetConfig): Date {
  if (config.startsAfter) {
    const parsed = new Date(config.startsAfter);
    if (!Number.isNaN(parsed.getTime())) {
      return new Date(
        parsed.getFullYear(),
        parsed.getMonth(),
        parsed.getDate(),
      );
    }
  }
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

export function EventListWidget({ config }: WidgetProps) {
  const [view, setView] = useState<View>(config.view);
  const [anchorDate, setAnchorDate] = useState<Date>(() =>
    initialAnchor(config),
  );
  const [query, setQuery] = useState<string>("");

  const [events, setEvents] = useState<EmbedEvent[]>([]);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

  const feedUrl = useMemo(
    () => buildFeedUrl(config, { view, anchorDate }),
    [config, view, anchorDate],
  );
  const isMonthly = view === "monthly";

  const loadMoreAbortRef = useRef<AbortController | null>(null);

  const visibleEvents = useMemo(
    () =>
      query ? events.filter((event) => matchesQuery(event, query)) : events,
    [events, query],
  );

  const selectedEvent = useMemo(
    () => events.find((event) => event.id === selectedEventId) ?? null,
    [events, selectedEventId],
  );

  useEffect(() => {
    const abortController = new AbortController();

    async function loadEvents() {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        if (isMonthly) {
          const all = await fetchEventsAllPages(
            feedUrl,
            abortController.signal,
          );
          if (abortController.signal.aborted) return;
          setEvents(all);
          setNextUrl(null);
        } else {
          const payload = await fetchEvents(feedUrl, abortController.signal);
          setEvents(payload.results ?? []);
          setNextUrl(payload.next ?? null);
        }
      } catch (error) {
        if (abortController.signal.aborted) return;
        const message =
          error instanceof Error ? error.message : "Unknown error";
        setErrorMessage(message);
      } finally {
        if (!abortController.signal.aborted) setIsLoading(false);
      }
    }

    void loadEvents();
    setSelectedEventId(null);
    setNextUrl(null);

    return () => {
      abortController.abort();
      loadMoreAbortRef.current?.abort();
      loadMoreAbortRef.current = null;
    };
  }, [feedUrl, isMonthly]);

  async function loadMore() {
    if (!nextUrl || isLoadingMore) return;
    loadMoreAbortRef.current?.abort();
    const ac = new AbortController();
    loadMoreAbortRef.current = ac;
    setIsLoadingMore(true);
    try {
      const response = await fetch(nextUrl, {
        headers: { Accept: "application/json" },
        signal: ac.signal,
      });
      if (ac.signal.aborted) return;
      if (!response.ok)
        throw new Error(`Request failed with status ${response.status}`);
      const payload = (await response.json()) as PaginatedResponse;
      if (ac.signal.aborted) return;
      setEvents((prev) => [...prev, ...(payload.results ?? [])]);
      setNextUrl(payload.next ?? null);
    } catch {
      // silently ignore — user can retry
    } finally {
      if (loadMoreAbortRef.current === ac) {
        loadMoreAbortRef.current = null;
        setIsLoadingMore(false);
      }
    }
  }

  const showPageTitle =
    config.showToolbar &&
    view !== "list" &&
    view !== "monthly" &&
    config.headerTitle !== "";

  const viewProps = {
    config,
    events: visibleEvents,
    isLoading,
    isLoadingMore,
    errorMessage,
    nextUrl,
    anchorDate,
    onSelect: setSelectedEventId,
    onLoadMore: loadMore,
  };

  return (
    <section className="gitg-widget" aria-live="polite">
      {config.showToolbar ? (
        <Toolbar
          config={config}
          view={view}
          onViewChange={setView}
          anchorDate={anchorDate}
          onAnchorDateChange={setAnchorDate}
          query={query}
          onQueryChange={setQuery}
        />
      ) : null}

      {showPageTitle ? (
        <h2 className="gitg-page-title">{config.headerTitle}</h2>
      ) : null}

      {view === "list" ? <ListView {...viewProps} /> : null}
      {view === "agenda" ? <AgendaView {...viewProps} /> : null}
      {view === "monthly" ? <MonthlyView {...viewProps} /> : null}
      {view === "pinboard" ? <PinboardView {...viewProps} /> : null}

      {selectedEvent && !isLoading && !errorMessage ? (
        <EventModal
          event={selectedEvent}
          config={config}
          onClose={() => setSelectedEventId(null)}
        />
      ) : null}
    </section>
  );
}
